/**
 * Component-supplied overrides for a `linkClick` GBAAL event. `interactionType` is
 * always required (GBAAL can't infer click vs. change from the element alone); `value`
 * should only be set when GBAAL's auto-derivation from the element's text/aria-label
 * would be incorrect (e.g. a generic "Wheel 1" label that doesn't reflect the real
 * option name).
 */
export interface GbaalLinkFieldOverrides {
	value?: string
	interactionType: 'click' | 'change'
}

/**
 * Dispatch a `gbaal:event` describing a `linkClick` interaction, passing the interacted
 * DOM element through as `detail.element` so GBAAL itself can derive `link.xPath`,
 * `link.moduleName`, `link.location`, and (absent an override) `link.value` from it —
 * the same way it already does for its document-level auto-collected clicks. This
 * component never duplicates that element-inspection logic; it only supplies what GBAAL
 * cannot infer on its own (`interactionType`, optional `value` override, and `vehicles`).
 *
 * Fire-and-forget: a no-op when GBAAL isn't loaded. `composed: true` is required so the
 * event escapes the Shadow DOM boundaries of nested custom elements.
 *
 * @param host - The custom element instance to dispatch the event from.
 * @param element - The DOM element the user interacted with (e.g. the clicked swatch input/button).
 * @param link - Component-known `link.*` overrides (`interactionType` required, `value` optional).
 * @param vehicles - Optional `vehicles.*` fields (e.g. `{ exteriorColor: '...' }`).
 */
export function dispatchGbaalLinkClickEvent(
	host: HTMLElement,
	element: Element,
	link: GbaalLinkFieldOverrides,
	vehicles?: Record<string, string>
): void {
	const detail = {
		event: 'linkClick',
		element,
		payload: {
			eventData: { name: 'link click', type: 'interaction' },
			link,
			...(vehicles && Object.keys(vehicles).length ? { vehicles } : {}),
		},
	}

	host.dispatchEvent(new CustomEvent('gbaal:event', { detail, bubbles: true, composed: true }))
}
